/**
 * Motion doctrine, as code (ART-BIBLE §7).
 *
 * The named constants below are the ONLY timings allowed in this game. If a motion needs a
 * number that is not here, either it is wrong or the doctrine needs a new constant — add it
 * here and to the bible, never inline.
 */

// ---------------------------------------------------------------- constants (seconds)

export const ANTIC = 0.090;    // anticipation wind-up before any committed motion
export const SNAP = 0.120;    // the state change itself
export const SETTLE = 0.380;    // overshoot → rest
export const BEAT = 0.500;    // the deadpan pause — comic timing unit
export const DRIFT = 3.200;    // idle breathing loop
export const FLICKER = 0.070;    // neon flicker quantum
export const RATCHET = 0.055;    // one ticket step / one reel detent
export const DOLLY = 0.700;    // camera move between aisle and cabinet
export const SLOWMO = 0.900;    // the Bank-sprengen gasp (at timescale 0.25)
export const SLOWMO_SCALE = 0.25;

// ---------------------------------------------------------------- easing

const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);

export const ease = {
  linear: (t: number) => t,
  inQuad: (t: number) => t * t,
  outQuad: (t: number) => 1 - (1 - t) * (1 - t),
  inOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  outCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  outQuint: (t: number) => 1 - Math.pow(1 - t, 5),
  inOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  /** the settle: overshoot 1.6 then come back. Nothing in this game stops dead. */
  outBack: (t: number, s = 1.6) => 1 + (s + 1) * Math.pow(t - 1, 3) + s * Math.pow(t - 1, 2),
  /** hard mechanical stop: overshoot, clunk back, two shivers at 20% and 6% */
  detent: (t: number) => {
    if (t >= 1) return 1;
    const d = 1 - t;
    return 1 - Math.cos(t * Math.PI * 3.6) * Math.pow(d, 2.4) * 0.34 - Math.pow(d, 1.2) * 0.0;
  },
  /** anticipation built in: pull back 12% then go */
  antic: (t: number) => {
    if (t < 0.22) return -0.12 * Math.sin((t / 0.22) * Math.PI);
    const u = (t - 0.22) / 0.78;
    return ease.outBack(u, 1.35);
  },
  /** rise, hold, fall — for flashes and vignettes */
  pulse: (t: number) => Math.sin(Math.PI * clamp01(t)),
};

export type Easing = (t: number) => number;

// ---------------------------------------------------------------- timeline

interface Span { t0: number; t1: number; fn: (u: number, dt: number) => void; ez: Easing; done?: boolean }
interface Call { at: number; fn: () => void; done?: boolean }

/**
 * A tiny timeline. Deliberately not a general animation engine: effects in this game are
 * short, scripted and readable top-to-bottom, which is how they stay on-doctrine.
 */
export class Timeline {
  private spans: Span[] = [];
  private calls: Call[] = [];
  private cursor = 0;
  time = 0;
  length = 0;
  finished = false;
  onDone?: () => void;

  /** run fn(u) from `at` for `dur`, easing u */
  at(at: number, dur: number, ez: Easing, fn: (u: number, dt: number) => void): this {
    this.spans.push({ t0: at, t1: at + dur, fn, ez });
    this.length = Math.max(this.length, at + dur);
    return this;
  }

  /** append after the current cursor and advance it */
  then(dur: number, ez: Easing, fn: (u: number, dt: number) => void): this {
    this.at(this.cursor, dur, ez, fn);
    this.cursor += dur;
    return this;
  }

  wait(d: number): this { this.cursor += d; this.length = Math.max(this.length, this.cursor); return this; }

  call(fn: () => void, at = this.cursor): this {
    this.calls.push({ at, fn });
    this.length = Math.max(this.length, at);
    return this;
  }

  update(dt: number): boolean {
    if (this.finished) return true;
    this.time += dt;
    for (const c of this.calls) {
      if (!c.done && this.time >= c.at) { c.done = true; c.fn(); }
    }
    for (const s of this.spans) {
      if (this.time < s.t0) continue;
      if (s.done) continue;
      const raw = (this.time - s.t0) / Math.max(1e-6, s.t1 - s.t0);
      if (raw >= 1) { s.fn(s.ez(1), dt); s.done = true; }
      else s.fn(s.ez(raw), dt);
    }
    if (this.time >= this.length && this.spans.every((s) => s.done) && this.calls.every((c) => c.done)) {
      this.finished = true;
      this.onDone?.();
    }
    return this.finished;
  }
}

/** the pile of running timelines */
export class Anim {
  private list: Timeline[] = [];
  /** big spectacles queue: only one at a time takes the stage (ART-BIBLE §7.3) */
  private queue: (() => Timeline)[] = [];
  private big: Timeline | null = null;

  play(tl: Timeline): Timeline { this.list.push(tl); return tl; }

  /** queue a camera-taking spectacle behind any other spectacle */
  playBig(make: () => Timeline): void {
    if (this.big && !this.big.finished) this.queue.push(make);
    else { this.big = this.play(make()); }
  }

  get busy(): boolean { return !!this.big && !this.big.finished; }
  get running(): number { return this.list.length; }

  update(dt: number): void {
    for (let i = this.list.length - 1; i >= 0; i--) {
      if (this.list[i].update(dt)) this.list.splice(i, 1);
    }
    if ((!this.big || this.big.finished) && this.queue.length) {
      this.big = this.play(this.queue.shift()!());
    }
  }

  clear(): void { this.list.length = 0; this.queue.length = 0; this.big = null; }
}

// ---------------------------------------------------------------- helpers

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** critically-damped follow — the camera's default motion, never a linear lerp */
export function damp(current: number, target: number, lambda: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-lambda * dt));
}

/** decaying shake, for cabinet kicks and Rütteln */
export function shake(t: number, dur: number, amp: number, freq = 34): number {
  if (t >= dur) return 0;
  const d = 1 - t / dur;
  return Math.sin(t * freq) * amp * d * d;
}
